/**
 * IFC quantity disambiguation (DEV-3445) and per-row measure overrides
 * (DEV-3577).
 *
 * An IFC-export row (e.g. from Solibri) carries several measure columns —
 * `Length`, `Area`, `Volume`, `Count` — of which only some are meaningful for a
 * given element. This module picks the single relevant measure per IFC type
 * using a fixed priority order and maps it to a Nodon unit. Pure domain logic;
 * the CSV pipeline wires it in (see `augmentWithIfcQuantities` in
 * `kalkylfil-csv.parser.ts`). Users can later override the chosen measure via
 * Quantity from file ({@link applyCandidateIfcMeasureOverride}).
 */
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { toNumber } from '@shared/helpers/math.helpers';
import type { CandidateRow } from '@shared/models/element-import.model';
import { selectableUnitHarmonizer } from './unit.helpers';

/** Local copy — avoid importing kalkylfil-csv.parser (circular with this module). */
const headerKey = (s: string): string => s.toLowerCase().trim();

/** A quantity column produced by an IFC export. */
export type IfcMeasure = 'Length' | 'Area' | 'Volume' | 'Count';

/** Stable menu / scan order for IFC measure columns. */
export const IFC_MEASURES: readonly IfcMeasure[] = [
  'Length',
  'Area',
  'Volume',
  'Count',
];

const IFC_MEASURE_SET: ReadonlySet<string> = new Set(IFC_MEASURES);

/** Narrow a string to {@link IfcMeasure}. */
export const isIfcMeasure = (value: string): value is IfcMeasure =>
  IFC_MEASURE_SET.has(value);

/** Nodon unit for each measure. */
export const UNIT_BY_MEASURE: Record<IfcMeasure, string> = {
  Length: 'm',
  Area: 'm2',
  Volume: 'm3',
  Count: 'pcs',
};

/** Display unit for a measure (e.g. `m3` → `m³`). */
export const displayUnitForIfcMeasure = (measure: IfcMeasure): string =>
  selectableUnitHarmonizer(UNIT_BY_MEASURE[measure]) ??
  UNIT_BY_MEASURE[measure];

/**
 * Header keys that mark an IFC-export layout (the column holding the IFC type).
 * Lower-cased/trimmed, matching `headerKey` in the CSV parser.
 */
export const IFC_TYPE_HEADER_KEYS: ReadonlySet<string> = new Set([
  'ifc entity',
  'ifc type',
]);

/** Measure -> its header key in an IFC export. */
export const IFC_MEASURE_HEADER_KEYS: Record<IfcMeasure, string> = {
  Length: 'length',
  Area: 'area',
  Volume: 'volume',
  Count: 'count',
};

/** `quantity_source` value flagging a row for manual control (unknown IFC type). */
export const MANUAL_QUANTITY_SOURCE = 'manual';

/**
 * Per-IFC-type measure priority. Each group lists every IFC type alias that
 * shares the same order; the first measure with a valid value wins. Types not
 * listed (`IfcTypeProduct`, `IfcBuildingElementProxy`, anything unknown) are
 * flagged for manual control. Source: the DEV-3445 priority table.
 */
const PRIORITY_GROUPS: readonly {
  types: readonly string[];
  priority: readonly IfcMeasure[];
}[] = [
  {
    types: ['IfcWall', 'IfcWallType'],
    priority: ['Area', 'Volume', 'Length', 'Count'],
  },
  {
    types: ['IfcCurtainWall', 'IfcCurtainWallType'],
    priority: ['Area', 'Count', 'Volume'],
  },
  { types: ['IfcSlab', 'IfcSlabType'], priority: ['Area', 'Volume', 'Count'] },
  { types: ['IfcRoof', 'IfcRoofType'], priority: ['Area', 'Volume', 'Count'] },
  {
    types: ['IfcDoor', 'IfcDoorType', 'IfcDoorStyle'],
    priority: ['Area', 'Count'],
  },
  {
    types: ['IfcWindow', 'IfcWindowType', 'IfcWindowStyle'],
    priority: ['Area', 'Count'],
  },
  {
    types: ['IfcBeam', 'IfcBeamType'],
    priority: ['Length', 'Volume', 'Count'],
  },
  {
    types: ['IfcColumn', 'IfcColumnType'],
    priority: ['Length', 'Volume', 'Count'],
  },
  {
    types: ['IfcMember', 'IfcMemberType'],
    priority: ['Length', 'Volume', 'Count'],
  },
  {
    types: ['IfcPile', 'IfcPileType'],
    priority: ['Length', 'Volume', 'Count'],
  },
  {
    types: ['IfcFooting', 'IfcFootingType'],
    priority: ['Volume', 'Area', 'Count'],
  },
  {
    types: ['IfcCovering', 'IfcCoveringType'],
    priority: ['Area', 'Count', 'Volume'],
  },
  {
    types: ['IfcStair', 'IfcStairType'],
    priority: ['Volume', 'Count', 'Length'],
  },
  {
    types: ['IfcStairFlight', 'IfcStairFlightType'],
    priority: ['Volume', 'Count', 'Length'],
  },
  {
    types: ['IfcRamp', 'IfcRampType', 'IfcRampFlight', 'IfcRampFlightType'],
    priority: ['Area', 'Volume', 'Count'],
  },
  {
    types: ['IfcRailing', 'IfcRailingType'],
    priority: ['Length', 'Count', 'Volume'],
  },
  {
    types: ['IfcPlate', 'IfcPlateType'],
    priority: ['Area', 'Volume', 'Count'],
  },
  {
    types: ['IfcTransportElement', 'IfcTransportElementType'],
    priority: ['Count', 'Volume'],
  },
  {
    types: [
      'IfcDuctSegment',
      'IfcDuctSegmentType',
      'IfcPipeSegment',
      'IfcPipeSegmentType',
      'IfcCableCarrierSegment',
      'IfcCableCarrierSegmentType',
    ],
    priority: ['Length', 'Volume', 'Count'],
  },
];

const normalizeIfcType = (raw: string): string => raw.trim().toLowerCase();

const PRIORITY_BY_TYPE: ReadonlyMap<string, readonly IfcMeasure[]> = new Map(
  PRIORITY_GROUPS.flatMap((group) =>
    group.types.map(
      (type) => [normalizeIfcType(type), group.priority] as const,
    ),
  ),
);

/**
 * A measure value is valid when it parses to a number strictly greater than
 * zero. Empty, `null`, non-numeric text and values ≤ 0 are treated as missing.
 * Uses {@link toNumber} so parsing matches the rest of the pipeline.
 */
export const isValidMeasure = (raw: string): boolean => toNumber(raw, 0) > 0;

export type IfcQuantityResult = {
  /** The chosen measure's raw value, or '' when none is valid / manual. */
  quantity: string;
  /** Nodon unit for the chosen measure, or '' when none. */
  unit: string;
  /** Chosen measure name, {@link MANUAL_QUANTITY_SOURCE}, or '' (hidden provenance). */
  source: string;
};

const EMPTY_RESULT: IfcQuantityResult = { quantity: '', unit: '', source: '' };

/**
 * Priority for a type the table does not list -- `IfcTypeProduct`,
 * `IfcBuildingElementProxy`, a blank IFC Type cell, anything unknown. These
 * rows carry perfectly good measure columns, so leaving them empty stranded
 * them on manual entry; the generic order picks the largest dimension the row
 * actually has (DEV-3603). The pick stays flagged as
 * {@link MANUAL_QUANTITY_SOURCE} -- it is a fallback, not a typed rule.
 */
export const DEFAULT_MEASURE_PRIORITY: readonly IfcMeasure[] = [
  'Area',
  'Volume',
  'Length',
  'Count',
];

const FALLBACK_PRIORITY = DEFAULT_MEASURE_PRIORITY;

/**
 * Pick the relevant quantity + unit for one IFC-export row. Walks the type's
 * priority order and returns the first measure with a valid value. A type
 * outside the table falls back to {@link FALLBACK_PRIORITY} and is flagged for
 * manual control; a row with no valid measure at all yields an empty result.
 */
export const deriveIfcQuantity = (
  ifcType: string,
  measures: Record<IfcMeasure, string>,
): IfcQuantityResult => {
  const typed = PRIORITY_BY_TYPE.get(normalizeIfcType(ifcType));
  const priority = typed ?? FALLBACK_PRIORITY;

  for (const measure of priority) {
    const raw = measures[measure] ?? '';
    if (isValidMeasure(raw)) {
      return {
        quantity: raw.trim(),
        unit: UNIT_BY_MEASURE[measure],
        source: typed ? measure : MANUAL_QUANTITY_SOURCE,
      };
    }
  }
  return typed
    ? EMPTY_RESULT
    : { ...EMPTY_RESULT, source: MANUAL_QUANTITY_SOURCE };
};

/**
 * The measure priority order the parser used for an IFC type -- the type's own
 * when the table lists it, else {@link FALLBACK_PRIORITY}. Exposed so consumers
 * that need the *other* measures on a row -- e.g. auto-switching the import
 * unit to one the mapped product/recipe can convert (DEV-3603) -- walk the same
 * order the parser used to pick the first one.
 */
export const getIfcMeasurePriority = (ifcType: string): readonly IfcMeasure[] =>
  PRIORITY_BY_TYPE.get(normalizeIfcType(ifcType)) ?? FALLBACK_PRIORITY;

/**
 * Read Length/Area/Volume/Count cell values from a candidate row. Prefers
 * {@link CandidateRow.cells} `originalHeader`, then falls back to `raw` keys
 * whose last segment matches the measure name (e.g. `_.Volume`).
 */
export const readIfcMeasuresFromRow = (
  row: CandidateRow,
): Record<IfcMeasure, string> => {
  const out: Record<IfcMeasure, string> = {
    Length: '',
    Area: '',
    Volume: '',
    Count: '',
  };
  for (const measure of IFC_MEASURES) {
    const key = headerKey(measure);
    const cell = row.cells.find((c) => headerKey(c.originalHeader) === key);
    if (cell) {
      out[measure] = String(cell.value ?? '');
      continue;
    }
    const rawKey = Object.keys(row.raw).find((k) => {
      const base = k.includes('.') ? k.slice(k.lastIndexOf('.') + 1) : k;
      return headerKey(base) === key;
    });
    if (rawKey !== undefined) out[measure] = String(row.raw[rawKey] ?? '');
  }
  return out;
};

/** Measures on {@link row} with a valid (> 0) numeric value. */
export const listAvailableIfcMeasures = (row: CandidateRow): IfcMeasure[] => {
  const measures = readIfcMeasuresFromRow(row);
  return IFC_MEASURES.filter((measure) => isValidMeasure(measures[measure]));
};

/**
 * Resolve quantity + unit for an explicit measure pick. Empty when the measure
 * is missing or invalid on the row.
 */
export const quantityFromIfcMeasure = (
  row: CandidateRow,
  measure: IfcMeasure,
): IfcQuantityResult => {
  const raw = readIfcMeasuresFromRow(row)[measure] ?? '';
  if (!isValidMeasure(raw)) return EMPTY_RESULT;
  return {
    quantity: raw.trim(),
    unit: UNIT_BY_MEASURE[measure],
    source: measure,
  };
};

/** Column name (left) + quantity and unit (right) for one IFC measure menu row. */
export type IfcMeasureMenuParts = {
  columnLabel: string;
  value: string;
};

/** Group heading above file-measure options in the Quantity menu. */
export const IFC_MEASURE_MENU_GROUP_LABEL = 'Quantity from file';

/**
 * Splits an IFC measure pick into a left column name (`Length`) and a right
 * quantity + unit (`12.3 m`). Value is empty when the measure is missing on
 * {@link row}.
 */
export const getIfcMeasureMenuParts = (
  row: CandidateRow,
  measure: IfcMeasure,
): IfcMeasureMenuParts => {
  const unit = displayUnitForIfcMeasure(measure);
  const { quantity } = quantityFromIfcMeasure(row, measure);
  if (!quantity) return { columnLabel: measure, value: '' };
  const parsed = toNumber(quantity, NaN);
  const qty = Number.isFinite(parsed)
    ? getDisplayNumber(parsed)
    : quantity.trim();
  return { columnLabel: measure, value: `${qty} ${unit}` };
};

/**
 * Searchable label for an IFC measure pick, e.g. `Area 30 m²`. Falls back to
 * the column name when the value is missing.
 */
export const formatIfcMeasureMenuLabel = (
  row: CandidateRow,
  measure: IfcMeasure,
): string => {
  const { columnLabel, value } = getIfcMeasureMenuParts(row, measure);
  return value ? `${columnLabel} ${value}` : columnLabel;
};

/**
 * First measure on {@link row} with a valid value, in
 * {@link DEFAULT_MEASURE_PRIORITY} order (Area → Volume → Length → Count).
 */
export const firstAvailableIfcMeasure = (
  row: CandidateRow,
): IfcMeasure | undefined => {
  const measures = readIfcMeasuresFromRow(row);
  return DEFAULT_MEASURE_PRIORITY.find((measure) =>
    isValidMeasure(measures[measure]),
  );
};

/**
 * Active IFC measure for a row: draft override when valid, else the synthetic
 * Quantity Source cell, else the measure whose unit matches the quantity unit
 * cell, else the first available measure in {@link DEFAULT_MEASURE_PRIORITY}.
 */
export const resolveActiveIfcMeasure = (
  row: CandidateRow,
  draftMeasure?: IfcMeasure,
): IfcMeasure | undefined => {
  const measures = readIfcMeasuresFromRow(row);
  if (draftMeasure && isValidMeasure(measures[draftMeasure])) {
    return draftMeasure;
  }

  const sourceCell = row.cells.find(
    (c) => headerKey(c.originalHeader) === 'quantity source',
  );
  const source = String(sourceCell?.value ?? '').trim();
  if (isIfcMeasure(source) && isValidMeasure(measures[source])) return source;

  const unitCell = row.cells.find((c) => c.nodonField === 'unit');
  const unit = String(unitCell?.value ?? '')
    .trim()
    .toLowerCase();
  if (unit) {
    const fromUnit = IFC_MEASURES.find(
      (measure) =>
        isValidMeasure(measures[measure]) &&
        UNIT_BY_MEASURE[measure].toLowerCase() === unit,
    );
    if (fromUnit) return fromUnit;
  }

  return firstAvailableIfcMeasure(row);
};

/**
 * The measure on {@link row} whose unit is {@link unit} -- how an already
 * imported element names the column its Mängd came from, given only the unit
 * its origin mapping is keyed by.
 */
export const ifcMeasureForUnit = (
  row: CandidateRow,
  unit: string | undefined,
): IfcMeasure | undefined => {
  if (!unit) return undefined;
  const measures = readIfcMeasuresFromRow(row);
  const wanted = unit.trim().toLowerCase();
  return IFC_MEASURES.find(
    (measure) =>
      isValidMeasure(measures[measure]) &&
      UNIT_BY_MEASURE[measure].toLowerCase() === wanted,
  );
};

/**
 * Return a copy of {@link row} whose quantity/unit (and Quantity Source) cells
 * reflect {@link measure}. No-op when measure is absent or invalid.
 */
export const applyCandidateIfcMeasureOverride = (
  row: CandidateRow,
  measure: IfcMeasure | undefined,
): CandidateRow => {
  if (!measure) return row;
  const resolved = quantityFromIfcMeasure(row, measure);
  if (!resolved.quantity || !resolved.unit) return row;

  return {
    ...row,
    cells: row.cells.map((cell) => {
      if (cell.nodonField === 'quantity') {
        return { ...cell, value: resolved.quantity };
      }
      if (cell.nodonField === 'unit') {
        return { ...cell, value: resolved.unit };
      }
      if (headerKey(cell.originalHeader) === 'quantity source') {
        return { ...cell, value: resolved.source };
      }
      return cell;
    }),
  };
};

/**
 * Row whose quantity/unit match the active IFC measure (draft pick, else the
 * derived source). Conversion-rate UI must use this so "per" follows Quantity
 * from file, not the parse-time default.
 */
export const rowWithActiveIfcMeasure = (
  row: CandidateRow,
  draftMeasure?: IfcMeasure,
): CandidateRow =>
  applyCandidateIfcMeasureOverride(
    row,
    resolveActiveIfcMeasure(row, draftMeasure),
  );

/**
 * {@link CandidateRow.raw} with quantity/unit (and Quantity Source) overwritten
 * to match {@link measure}, for the import metadata strip. Overrides live on
 * cells only; without this the strip would keep showing parse-time values.
 */
export const rawWithActiveIfcQuantity = (
  row: CandidateRow,
  measure: IfcMeasure | undefined,
): Record<string, string | number> => {
  const effective = applyCandidateIfcMeasureOverride(row, measure);
  if (effective === row) return row.raw;

  const next: Record<string, string | number> = { ...row.raw };
  for (const cell of effective.cells) {
    const isQtyOrUnit =
      cell.nodonField === 'quantity' || cell.nodonField === 'unit';
    const isSource = headerKey(cell.originalHeader) === 'quantity source';
    if (!isQtyOrUnit && !isSource) continue;
    next[cell.normalizedHeader] = cell.value;
  }
  return next;
};
